// ** React Imports
import { useState, ChangeEvent } from 'react'

// ** Next Import
import { useRouter } from 'next/router'

// ** MUI Imports
import { styled } from '@mui/material/styles'
import ListItem from '@mui/material/ListItem'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import CardContent from '@mui/material/CardContent'
import ListItemButton from '@mui/material/ListItemButton'
import InputAdornment from '@mui/material/InputAdornment'
import MuiAutocomplete, {
  AutocompleteRenderInputParams
} from '@mui/material/Autocomplete'
import { Button, Card, Chip, IconButton, Stack } from '@mui/material'
import { InputNumber, message, Select } from 'antd'

// ** Icon Imports
import Icon from 'src/@core/components/icon'
import { useMutation, useQuery } from 'react-query'
import { fetchSample, getGeneByHpoIds } from '@/@core/services/app.service'
import Box from '@mui/material/Box'
import { isWindow } from '@/@core/utils/utils'
import { useTranslation } from 'react-i18next'
import { ContentCopy, Search } from '@mui/icons-material'
import React from 'react'
import ChinahpoSearch from '@/@core/components/chinahpo-search'
import { copyToClipboard } from '@/@core/layouts/utils'

// Styled Autocomplete component
const Autocomplete = styled(MuiAutocomplete)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    paddingLeft: theme.spacing(3.5),
    backgroundColor: theme.palette.background.paper
  },
  [theme.breakpoints.up('md')]: {
    width: '55%'
  },
  [theme.breakpoints.up('xl')]: {
    width: '45%'
  },
  [theme.breakpoints.down('md')]: {
    width: '75%'
  },
  [theme.breakpoints.down('sm')]: {
    width: '100%'
  }
}))

const geneName_List = [] as string[]

const links = [
  {
    name: 'Clinvar',
    url: 'https://www.ncbi.nlm.nih.gov/clinvar/',
    icon: '/images/links/clinvar.png'
  },

  // {
  //   name: 'Franklin',
  //   url: 'https://franklin.genoox.com/clinical-db/home',
  //   icon: '/images/links/franklin.png'
  // },
  // {
  //   name: 'Varsome',
  //   url: 'https://varsome.com/',
  //   icon: '/images/links/varsome.png'
  // },
  {
    name: 'Clingen',
    url: 'https://clinicalgenome.org/',
    icon: '/images/links/clingen.png'
  },
  {
    name: 'OMIM',
    url: 'https://www.omim.org/',
    icon: '/images/links/th.png'
  },
  {
    name: 'Orphanet',
    url: 'https://www.orpha.net/consor/cgi-bin/Disease_Search.php?lng=EN&data_id=20070&Disease_Disease_Search_diseaseGroup=FLG&Disease_Disease_Search_diseaseType=Gen&Disease(s)/group%20of%20diseases=Peeling-skin-syndrome-type-A&title=Peeling%20skin%20syndrome%20type%20A&search=Disease_Search_Simple',
    icon: '/images/links/orphanet.png'
  },
  {
    name: 'Decipher',
    url: 'https://www.deciphergenomics.org/',
    icon: '/images/links/decipher.png'
  },
  {
    name: 'HGMD',
    url: 'https://www.hgmd.cf.ac.uk/ac/index.php',
    icon: '/images/links/HGMD.png'
  },
  {
    name: 'Mastermind',
    url: 'https://mastermind.genomenon.com/',
    icon: '/images/links/mastermind.png'
  },
  {
    name: 'LOVD',
    url: 'https://www.lovd.nl/',
    icon: '/images/links/lovd.png'
  },
  {
    name: 'gnomAD',
    url: 'https://gnomad.broadinstitute.org/',
    icon: '/images/links/th.png'
  },
  {
    name: 'UCSC',
    url: 'https://genome.ucsc.edu/',
    icon: '/images/links/ucsc.png'
  },
  {
    name: 'EMBL-EBI',
    url: 'https://www.ebi.ac.uk/gxa/home/',
    icon: '/images/links/apple.png'
  },
  {
    name: 'SomaMutDB',
    url: 'https://somamutdb.org/SomaMutDB/',
    icon: '/images/links/smile2.png'
  },
  {
    name: 'ClinMAVE',
    url: 'https://ngdc.cncb.ac.cn/clinmave/',
    icon: '/images/links/clinmave.ico'
  }
]

interface Option {
  name: string
}

interface Item {
  HPO_id: string
  gene_names: string[]
}

const DashboardSearch = (props: { samplesList: any }) => {
  // ** States
  const [messageApi, contextHolder] = message.useMessage()
  const [value, setValue] = useState<string>('')
  const [name, setName] = useState<string>('')
  const [open, setOpen] = useState<boolean>(false)
  const { t } = useTranslation()
  const [search, setSearch] = React.useState<string>('')
  const [sampleList, setSampleList] = useState<any[]>([])

  // const [hpoList, setHpoList] = useState<any[]>([])
  const [termList, setTermList] = useState<any[]>([])
  const [geneList, setGeneList] = useState<Item[]>([])

  // ** Hooks & Vars
  const router = useRouter()

  const handleRedirection = (option: any) => {
    setOpen(false)

    setValue(option.title)
    let currentSubcategory

    if (currentSubcategory !== null) {
      router.push(`/samples/SampleTabs/${option.id}/`)
    }
  }

  const fetchSampleList = useMutation({
    mutationFn: async (data: any) => await fetchSample(data),
    onSuccess: (data) => {
      //

      if (data && data.list && Array.isArray(data.list)) {
        setSampleList(data.list)
      }
    }
  })

  const fetchGeneByHpoIds = useMutation({
    mutationFn: async (data: string[]) =>
      await getGeneByHpoIds({
        hpoIds: data
      }),
    onSuccess: (data) => {
      //

      if (data && Array.isArray(data) && data.length) {
        setGeneList(data)

        messageApi.warning('搜索成功')
      } else {
        messageApi.warning('暂无数据，请重试')
      }
    },
    onError: () => {
      messageApi.error('搜索失败，请重试')
    }
  })

  return (
    <Card sx={{ position: 'relative', height: '100%' }}>
      {contextHolder}

      <CardContent
        sx={{
          position: 'relative',
          backgroundSize: 'cover',
          height: '100%',
          borderRadius: '6px',
          pb: (theme) => `${theme.spacing(15)} !important`,
          background: ' url(/images/pages/Generic-DNA_hero2.png) no-repeat',
          WebkitBackgroundSize: 'cover',
          backgroundPosition: 'center',
          zIndex: 1,
          '::after': {
            content: "''",
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 2,
            background:
              'linear-gradient(to bottom, rgba(255,255,255,1), rgba(255,255,255,0.9), rgba(255,255,255,0))'
          }
        }}
      >
        <Box
          sx={{
            zIndex: 99,
            display: 'flex',
            textAlign: 'center',
            alignItems: 'center',
            flexDirection: 'column',
            position: 'relative',
            height: '100%',
            justifyContent: 'center'
          }}
        >
          {/* <Box width={'100%'} textAlign={'left'} pl={50}>
            <Button
              variant="contained"
              onClick={() => {
                window.open('https://www.glsofort.com/glsanger/')
              }}
              sx={{
                textTransform: 'capitalize'
              }}
            >
              {t('sanger generation tool')}
            </Button>
          </Box> */}

          <Stack
            spacing={{ xs: 1, sm: 2 }}
            direction="row"
            useFlexGap
            flexWrap="wrap"
            px={isWindow() ? 50 : 0}
          >
            {links.map((link) => (
              <Button
                variant="outlined"
                href={link.url}
                color="inherit"
                target="_blank"
                sx={{
                  flex: isWindow()
                    ? '1 0 calc(15% - 16px)'
                    : '1 0 calc(45% - 16px)',
                  mt: 4,
                  fontSize: 16,
                  mx: 6
                }}
                fullWidth
              >
                <img
                  src={link.icon}
                  alt="delete"
                  style={{ width: 24, height: 24, marginRight: 5 }}
                ></img>

                {link.name}
              </Button>
            ))}
          </Stack>

          <Box
            sx={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column'
            }}
          >
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                mt: 10,
                justifyContent: 'center'
              }}
            >
              <Box
                sx={{
                  width: isWindow() ? '40%' : '30%',
                  height: '2rem',
                  background: ' url(/images/pages/union.svg) no-repeat',
                  backgroundPosition: 'center, center'
                }}
              ></Box>

              <Typography
                variant="h5"
                sx={{
                  textAlign: 'center',
                  color: 'primary.main',
                  fontWeight: 600,
                  position: 'relative',
                  mx: 4
                }}
              >
                {t('Gene retrieval')}
              </Typography>

              <Box
                sx={{
                  width: isWindow() ? '40%' : '30%',
                  height: '2rem',
                  background: ' url(/images/pages/union2.svg) no-repeat',
                  backgroundPosition: 'center, center'
                }}
              ></Box>
            </Box>

            <Box
              width={'100%'}
              sx={{
                display: 'flex',
                justifyContent: 'center',
                flexDirection: 'column',
                alignItems: 'center'
              }}
            >
              <Stack
                width={'100%'}
                direction="row"
                justifyContent={'center'}
                alignItems="center"
              >
                {/* <Select
                  size="large"
                  style={{
                    width: '40%'
                  }}
                  placeholder={t(
                    'Please enter HP:xxxx and click the search button on the right'
                  )}
                  optionFilterProp="label"
                  onChange={(value: string[]) => {
                    //

                    if (!value.length) setGeneList([])

                    setHpoList(value)

                    geneName_List = []
                  }}
                  mode="multiple"
                  allowClear
                  value={hpoList}
                  options={geneName_List.map((item) => {
                    return {
                      value: item,
                      label: item
                    }
                  })}
                  searchValue={search}
                  onSearch={(e) => {
                    const arrValue = e.split(' ').join(',').split(',')

                    //

                    setSearch(e)

                    arrValue
                      .filter((item) => item)
                      .forEach((item) => {
                        if (geneName_List.find((i) => i === item)) {
                          //
                        } else {
                          geneName_List.push(item.toUpperCase())
                        }
                      })

                    if (e.includes(',') || e.includes(' ')) {
                      setHpoList(
                        Array.from(
                          new Set([
                            ...arrValue.filter((item) => item),
                            ...hpoList
                          ])
                        )
                      )

                      setSearch('')
                    }
                  }}
                  onInputKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      //

                      setHpoList(
                        Array.from(
                          new Set([
                            geneName_List[
                              geneName_List.length - 1
                            ].toUpperCase(),
                            ...hpoList
                          ])
                        ).filter((item) => item)
                      )

                      setSearch('')

                      geneName_List = []
                    }
                  }}
                  maxTagCount={14}
                /> */}
                <Box width={'40%'}>
                  <ChinahpoSearch
                    termList={termList}
                    fn={(query: any) => {
                      setTermList(query)

                      if (!query.length) setGeneList([])

                      return query
                    }}
                    sx={{
                      flex: 1,
                      width: '100%',
                      '& .MuiInputBase-root': {
                        alignItems: 'flex-start'
                      },
                      '& .MuiInputBase-input': {
                        '::placeholder': {
                          color: 'red'
                        }
                      },
                      '& #asynchronous-demo': {
                        height: 'initial !important',
                        minHeight: '36px'
                      }
                    }}
                  />
                </Box>

                <Button
                  color="primary"
                  size="large"
                  onClick={() => {
                    if (termList.filter((i) => i && i.hpo_code).length) {
                      const hpoIds = termList
                        .filter((i) => i && i.hpo_code)
                        .map((i) => i.hpo_code)

                      fetchGeneByHpoIds.mutateAsync(hpoIds)
                    }
                  }}
                >
                  <Search />
                </Button>
              </Stack>

              <Stack pt={2} maxHeight={200} width="44%" spacing={4}>
                {geneList.map((item) => (
                  <Stack
                    direction={'row'}
                    alignItems="center"
                    justifyContent="start"
                    spacing={2}
                  >
                    <Typography
                      key={`${item.HPO_id}`}
                      color={'primary.main'}
                      py={1}
                    >
                      {item.HPO_id}:
                    </Typography>

                    <Stack
                      direction={'row'}
                      alignItems="center"
                      justifyContent="space-between"
                      spacing={2}
                      maxWidth={'90%'}
                      overflow="auto"
                      py={4}
                    >
                      {item.gene_names
                        ? item.gene_names.map((gene) => {
                            return (
                              <Chip
                                label={gene}
                                key={`${item.HPO_id}-${gene}`}
                                color="primary"
                              />
                            )
                          })
                        : null}
                    </Stack>

                    <IconButton
                      onClick={(e) => {
                        //
                        try {
                          copyToClipboard(item.gene_names.join(';'), e)

                          messageApi.success(t('Successful copy'))
                        } catch (error) {
                          //

                          messageApi.success(t('Failed to copy'))
                        }
                      }}
                    >
                      <ContentCopy />
                    </IconButton>
                  </Stack>
                ))}
              </Stack>

              {/* <Button
            onClick={async () => {
              const query = {
                name: name,
                pageNum: 1,
                pageSize: 10
              }
              if (name) await fetchSampleList.mutateAsync(query)
            }}
            startIcon={<Search />}
            variant="contained"
            size="large"
          >
            {t('Search')}
          </Button> */}
            </Box>
          </Box>
        </Box>

        {/* <Typography variant="body2"></Typography> */}
      </CardContent>
    </Card>
  )
}

export default DashboardSearch
